// src/components/packages/PackageDetail.tsx

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { PackageResponse, PackageStatus, AddLocationCommand, PACKAGE_STATUS_LABELS } from '../../types/package.types';
import { StatusBadge } from '../ui/StatusBadge';
import { RoleGuard } from '../ui/RoleGuard';

interface Props {
  pkg: PackageResponse;
  onClose: () => void;
  onUpdateStatus: (id: string, status: PackageStatus) => Promise<boolean>;
  onAddLocation: (id: string, cmd: AddLocationCommand) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  isLoading: boolean;
}

const STATUSES = Object.keys(PACKAGE_STATUS_LABELS) as PackageStatus[];

export const PackageDetail: React.FC<Props> = ({
  pkg, onClose, onUpdateStatus, onAddLocation, onDelete, isLoading,
}) => {
  const [tab, setTab] = useState<'info' | 'locations' | 'actions'>('info');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } =
    useForm<AddLocationCommand>();

  const handleAddLocation = async (data: AddLocationCommand) => {
    const ok = await onAddLocation(pkg.id, data);
    if (ok) reset();
  };

  const handleDelete = async () => {
    const ok = await onDelete(pkg.id);
    if (ok) onClose();
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="pkg-detail">
      {/* Header */}
      <div className="pkg-detail__header">
        <div>
          <p className="pkg-detail__id">#{pkg.id}</p>
          <StatusBadge status={pkg.status} />
        </div>
        <button className="pkg-detail__close" onClick={onClose} aria-label="Cerrar">×</button>
      </div>

      {/* Tabs */}
      <div className="pkg-detail__tabs">
        {(['info', 'locations', 'actions'] as const).map(t => (
          <button
            key={t}
            className={`pkg-detail__tab${tab === t ? ' pkg-detail__tab--active' : ''}`}
            onClick={() => setTab(t)}
          >
            {{ info: 'Información', locations: 'Historial', actions: 'Acciones' }[t]}
          </button>
        ))}
      </div>

      <div className="pkg-detail__body">

        {/* ── INFO TAB ── */}
        {tab === 'info' && (
          <div className="pkg-detail__section">
            <h4 className="pkg-detail__section-title">Destinatario</h4>
            <dl className="pkg-detail__dl">
              <dt>Nombre</dt>    <dd>{pkg.recipient.name}</dd>
              <dt>Correo</dt>    <dd>{pkg.recipient.email}</dd>
              <dt>Teléfono</dt>  <dd>{pkg.recipient.phone}</dd>
              <dt>Dirección</dt> <dd>{pkg.recipient.address.fullAddress || `${pkg.recipient.address.street}, ${pkg.recipient.address.city}, ${pkg.recipient.address.country}`}</dd>
            </dl>

            <h4 className="pkg-detail__section-title">Dimensiones</h4>
            <dl className="pkg-detail__dl pkg-detail__dl--grid">
              <dt>Alto</dt>       <dd>{pkg.dimensions.height} cm</dd>
              <dt>Ancho</dt>      <dd>{pkg.dimensions.width} cm</dd>
              <dt>Profundidad</dt><dd>{pkg.dimensions.depth} cm</dd>
              <dt>Volumen</dt>    <dd>{pkg.dimensions.volume?.toFixed(2)} cm³</dd>
              <dt>Peso</dt>       <dd>{pkg.weight} kg</dd>
            </dl>

            {pkg.notes && (
              <>
                <h4 className="pkg-detail__section-title">Notas</h4>
                <p className="pkg-detail__notes">{pkg.notes}</p>
              </>
            )}

            <h4 className="pkg-detail__section-title">Fechas</h4>
            <dl className="pkg-detail__dl">
              <dt>Creado</dt>      <dd>{fmt(pkg.createdAt)}</dd>
              <dt>Actualizado</dt> <dd>{fmt(pkg.updatedAt)}</dd>
            </dl>
          </div>
        )}

        {/* ── LOCATIONS TAB ── */}
        {tab === 'locations' && (
          <div className="pkg-detail__section">
            {pkg.locations?.length > 0 ? (
              <ol className="pkg-timeline">
                {[...pkg.locations].reverse().map((loc, i) => (
                  <li key={i} className="pkg-timeline__item">
                    <div className="pkg-timeline__dot" />
                    <div className="pkg-timeline__content">
                      <p className="pkg-timeline__place">{loc.city}, {loc.country}</p>
                      <p className="pkg-timeline__desc">{loc.description}</p>
                      <p className="pkg-timeline__time">{fmt(loc.timestamp)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="pkg-detail__empty">Sin registros de ubicación aún.</p>
            )}

            {/* Add location form — ADMIN & OPERATOR only */}
            <RoleGuard roles={['ADMIN', 'OPERATOR']}>
              <h4 className="pkg-detail__section-title" style={{ marginTop: '1.5rem' }}>
                Agregar ubicación
              </h4>
              <form onSubmit={handleSubmit(handleAddLocation)} className="pkg-loc-form">
                <div className="pkg-form__grid pkg-form__grid--2">
                  <div className={`pkg-field ${errors.city ? 'pkg-field--error' : ''}`}>
                    <label className="pkg-field__label">Ciudad</label>
                    <input className="pkg-input" placeholder="Bogotá"
                      {...register('city', { required: 'Requerido' })} />
                    {errors.city && <p className="pkg-field__error">{errors.city.message}</p>}
                  </div>
                  <div className={`pkg-field ${errors.country ? 'pkg-field--error' : ''}`}>
                    <label className="pkg-field__label">País</label>
                    <input className="pkg-input" placeholder="Colombia"
                      {...register('country', { required: 'Requerido' })} />
                    {errors.country && <p className="pkg-field__error">{errors.country.message}</p>}
                  </div>
                </div>
                <div className="pkg-field">
                  <label className="pkg-field__label">Descripción</label>
                  <input className="pkg-input" placeholder="Paquete recibido en bodega"
                    {...register('description', { required: 'Requerido' })} />
                </div>
                <button type="submit" className="lgt-btn lgt-btn--primary" disabled={isLoading}>
                  {isLoading ? <><span className="lgt-btn__spinner" /> Guardando...</> : 'Registrar ubicación'}
                </button>
              </form>
            </RoleGuard>
          </div>
        )}

        {/* ── ACTIONS TAB ── */}
        {tab === 'actions' && (
          <div className="pkg-detail__section">
            <RoleGuard roles={['ADMIN', 'OPERATOR']}>
              <h4 className="pkg-detail__section-title">Cambiar estado</h4>
              <div className="pkg-status-grid">
                {STATUSES.map(s => (
                  <button
                    key={s}
                    className={`pkg-status-btn${pkg.status === s ? ' pkg-status-btn--active' : ''}`}
                    onClick={() => onUpdateStatus(pkg.id, s)}
                    disabled={isLoading || pkg.status === s}
                  >
                    {PACKAGE_STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </RoleGuard>

            <RoleGuard roles={['ADMIN']}>
              <h4 className="pkg-detail__section-title" style={{ marginTop: '2rem', color: 'var(--error)' }}>
                Zona de peligro
              </h4>
              {!confirmDelete ? (
                <button
                  className="lgt-btn lgt-btn--danger"
                  onClick={() => setConfirmDelete(true)}
                >
                  Eliminar paquete
                </button>
              ) : (
                <div className="pkg-detail__confirm">
                  <p>¿Estás seguro? Esta acción no se puede deshacer.</p>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                    <button className="lgt-btn lgt-btn--ghost" onClick={() => setConfirmDelete(false)}>
                      Cancelar
                    </button>
                    <button className="lgt-btn lgt-btn--danger" onClick={handleDelete} disabled={isLoading}>
                      {isLoading ? <><span className="lgt-btn__spinner" /> Eliminando...</> : 'Sí, eliminar'}
                    </button>
                  </div>
                </div>
              )}
            </RoleGuard>
          </div>
        )}
      </div>
    </div>
  );
};
